import i18n from '@/utils/i18n';
import React, { useMemo } from 'react';
import { AvailabilityMap, ItemListOption } from '@/contexts/item-context-types';
import { Button } from '@/components/button';
import { createStyles } from '@/contexts/theme-context';
import {
  FONTS,
  LIST_MARGIN_HORIZONTAL,
  SCREEN_PADDING,
  TYPOGRAPHY,
} from '@/styles';
import { getMinAndMaxQuantity } from '@/contexts/item-context-utils';
import { ItemWithSubstitute } from '@/components/orders/substitution-item-wrapper';
import { ListItemSeparator } from '@/components/list-item';
import { OrderableItemList } from '@/components/orders/orderable-item-list';
import { OrderableItemListItem } from '@/components/orders/orderable-item-list-item';
import {
  OrderQuantityActions,
  useQuantityReducer,
} from '@/screens/new-order/hooks/use-quantity-reducer';
import { SubstitutionAction } from '@/enums';
import { SubstitutionChoice } from './types';
import { Text, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useSetItemSubstitution } from './hooks/use-set-substitution';
import { useSmallLayoutSizeValue } from '@/contexts/layout-context';
import { useToasts } from '@/contexts/toast-context';
import { useVariantPackInfoContext } from '@/contexts/variant-pack-info-context';

export interface Props {
  itemWithSubstitute: ItemWithSubstitute<ItemListOption>;
  onSetSubstitution: (selectedSubstitutions: SubstitutionChoice[]) => void;
  orderUUID: string;
  substituteListOption: ItemListOption;
  substitutionAvailableQuantity: number | undefined;
}

export const SelectSubstitutionQuantityContent: React.FC<Props> = ({
  itemWithSubstitute,
  onSetSubstitution,
  orderUUID,
  substituteListOption,
  substitutionAvailableQuantity,
}) => {
  const { getVariantPackInfo } = useVariantPackInfoContext();

  const [styles] = useStyles();
  const { showErrorToast } = useToasts();

  const originalListOption = itemWithSubstitute.item;
  const originalVariantPack =
    itemWithSubstitute.substitution!.original_variant_pack;

  const { minimumQuantity } = getMinAndMaxQuantity(
    getVariantPackInfo(substituteListOption.metadata!.variantPackUUID),
  );

  // ensure minQuantity is a number and greater than 0 to set initial value
  const initialQuantityValue =
    typeof minimumQuantity === 'number' ? Math.max(minimumQuantity, 1) : 1;

  const [state, dispatch] = useQuantityReducer({
    eventPayloadMap: {},
    localQuantityMap: {
      [originalListOption.metadata!.variantPackUUID]: 0,
      [substituteListOption.metadata!.variantPackUUID]: initialQuantityValue,
    },
    savedQuantityMap: {},
    variantPackDetailMap: {
      [originalListOption.metadata!.variantPackUUID]: {
        itemDisplayName: originalListOption.metadata!.displayName,
        metadata: originalListOption.metadata!.variantPackMetadata,
      },
      [substituteListOption.metadata!.variantPackUUID]: {
        itemDisplayName: substituteListOption.metadata!.displayName,
        metadata: substituteListOption.metadata!.variantPackMetadata,
      },
    },
  });

  const canSaveSelection = useMemo(() => {
    return Object.values(state.localQuantityMap).some(
      (quantity) => quantity > 0,
    );
  }, [state.localQuantityMap]);

  const { setItemSubstitution, loading } = useSetItemSubstitution();

  const { bottom } = useSafeAreaInsets();

  const buttonContainerStyle = useSmallLayoutSizeValue(
    styles.buttonContainerSmall,
    styles.buttonContainerLarge,
  );
  const baseMarginBottom = useSmallLayoutSizeValue(
    Math.max(bottom, SCREEN_PADDING),
    0,
  );

  const availabilityMap: AvailabilityMap = {
    [originalListOption.metadata!.variantPackUUID]:
      originalVariantPack.available_quantity,
    ...(typeof substitutionAvailableQuantity === 'number'
      ? {
          [substituteListOption.metadata!.variantPackUUID]:
            substitutionAvailableQuantity,
        }
      : {}),
  };

  return (
    <View style={[styles.base, { marginBottom: baseMarginBottom }]}>
      <OrderableItemList
        gridColumnCount={1}
        listItemProps={{
          renderListItemFooter: (option) =>
            option.value === originalListOption.value ? (
              <ListItemSeparator style={styles.listItemSeparator} />
            ) : null,
          renderMainContent: (option) => {
            const renderingOriginalItem =
              option.value === originalListOption.value;
            return (
              <OrderableItemListItem
                availabilityMap={availabilityMap}
                canDecrementToZero={renderingOriginalItem}
                isFocused={false}
                option={option}
                quantityState={state}
                showTooltipOnMount
                source="substitutions_list"
                updateQuantity={({
                  eventPayloads,
                  newQuantity,
                  previousQuantity,
                  variantPackUUID,
                }) => {
                  dispatch({
                    payload: {
                      eventPayloads,
                      newQuantity,
                      previousQuantity,
                      source: 'substitutions_list',
                      variantPackDetails: {
                        itemDisplayName: option.metadata!.displayName,
                        metadata: option.metadata!.variantPackMetadata,
                      },
                      variantPackUUID,
                    },
                    type: OrderQuantityActions.UPDATE_QUANTITY,
                  });
                }}
              />
            );
          },
        }}
        renderSectionHeader={({ title }) => {
          if (!title) {
            return null;
          }
          return (
            <View style={styles.headerContainer}>
              <Text style={[TYPOGRAPHY.small, styles.headerText]}>{title}</Text>
            </View>
          );
        }}
        scrollViewProps={{
          alwaysBounceVertical: false,
        }}
        sections={[
          { data: [originalListOption], key: i18n.t('original_item') },
          { data: [substituteListOption], key: i18n.t('substitute') },
        ]}
        stickyHeaderIndices={[]}
      />
      <Button
        containerStyle={buttonContainerStyle}
        disabled={!canSaveSelection}
        loading={loading}
        onPress={async () => {
          try {
            await setItemSubstitution({
              action: SubstitutionAction.SUBSTITUTE,
              orderUUID,
              originalVariantPack: originalVariantPack,
              quantityMap: state.localQuantityMap,
            });
            const originalItem: SubstitutionChoice = {
              availableQuantity: originalVariantPack.available_quantity,
              itemListOption: originalListOption,
              quantity:
                state.localQuantityMap[
                  originalListOption.metadata!.variantPackUUID
                ],
            };
            const subItem: SubstitutionChoice = {
              availableQuantity: substitutionAvailableQuantity,
              itemListOption: substituteListOption,
              quantity:
                state.localQuantityMap[
                  substituteListOption.metadata!.variantPackUUID
                ],
            };
            onSetSubstitution(
              originalItem.quantity === 0 ? [subItem] : [originalItem, subItem],
            );
          } catch (e) {
            showErrorToast();
          }
        }}
        trackingData={{
          name: 'choose_substitution_quantity_button',
          properties: {
            availableQuantity: originalVariantPack.available_quantity,
            originalVariantPackUUID: originalVariantPack.uuid,
            substitutionAvailableQuantity,
            substitutionVariantPackUUID:
              substituteListOption.metadata!.variantPackUUID,
          },
        }}
      >
        {i18n.t('continue')}
      </Button>
    </View>
  );
};

const useStyles = createStyles((theme) => ({
  base: {
    flex: 1,
  },
  buttonContainerLarge: {
    marginHorizontal: LIST_MARGIN_HORIZONTAL,
  },
  buttonContainerSmall: {
    marginHorizontal: SCREEN_PADDING,
  },
  headerContainer: {
    marginHorizontal: LIST_MARGIN_HORIZONTAL,
  },
  headerText: {
    ...FONTS.primarySemiBold,
    color: theme.colors.inactiveText,
    textTransform: 'uppercase',
  },
  listItemSeparator: {
    marginBottom: SCREEN_PADDING,
    marginHorizontal: 8,
  },
}));
